import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import api, { crmApi, ownerWithdrawalsApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  DollarSign,
  CreditCard,
  Award,
  Clock,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Target,
  Percent,
  Briefcase,
  Wallet,
  Calendar,
  PiggyBank,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Database,
  Banknote,
  Trash2,
} from "lucide-react";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const MySalaryPage = () => {
  const { user } = useAuth();
  const { convertAmount, getCurrencySymbol, displayCurrency } = useCurrency();
  
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [crmBonus, setCrmBonus] = useState(null);
  const [companyProfit, setCompanyProfit] = useState(null);
  
  // Month selector for company profit
  const now = new Date();
  const [profitYear, setProfitYear] = useState(now.getFullYear());
  const [profitMonth, setProfitMonth] = useState(now.getMonth() + 1);
  const [profitLoading, setProfitLoading] = useState(false);
  const navigate = useNavigate();
  const [withdrawals, setWithdrawals] = useState([]);
  // Task-level fees addressed to me — executor work and the art-director fee.
  // Nobody outside role=artist had anywhere to see these. backlog 547fbd59.
  const [taskFees, setTaskFees] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);

  const profitMonthKey = useMemo(() => {
    return `${profitYear}-${String(profitMonth).padStart(2, "0")}`;
  }, [profitYear, profitMonth]);

  useEffect(() => {
    fetchProfile();
    api.get("/me/task-fees")
      .then((r) => setTaskFees(r.data))
      .catch(() => setTaskFees(null));
  }, []);
  
  // Fetch profit data and withdrawals when month changes
  useEffect(() => {
    if (profile?.user?.role === "admin") {
      fetchProfitData();
      fetchWithdrawals();
    }
  }, [profitMonthKey, profile?.user?.role]);

  const fetchProfitData = async () => {
    try {
      setProfitLoading(true);
      const profitRes = await api.get(`/stats/owner-dashboard?month=${profitMonthKey}`);
      setCompanyProfit(profitRes.data);
    } catch (err) {
      console.error("Error fetching company profit:", err);
    } finally {
      setProfitLoading(false);
    }
  };

  const fetchWithdrawals = async () => {
    try {
      const res = await ownerWithdrawalsApi.getAll({ year: profitYear });
      setWithdrawals(res.data || []);
    } catch (err) {
      console.error("Error fetching withdrawals:", err);
    }
  };

  const handleWithdraw = async (amount, type = "profit_80") => {
    if (!amount || amount <= 0) {
      toast.error("Invalid amount");
      return;
    }
    setWithdrawing(true);
    try {
      await ownerWithdrawalsApi.create({
        amount,
        currency: "USD",
        month: profitMonthKey,
        withdrawal_type: type,
        notes: type === "profit_80" ? "80% profit withdrawal" : "20% development fund withdrawal"
      });
      toast.success(`Withdrawal of $${amount.toLocaleString()} recorded`);
      fetchWithdrawals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record withdrawal");
    } finally {
      setWithdrawing(false);
    }
  };

  const handleDeleteWithdrawal = async (id) => {
    if (!window.confirm("Delete this withdrawal?")) return;
    try {
      await ownerWithdrawalsApi.delete(id);
      toast.success("Withdrawal deleted");
      fetchWithdrawals();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get("/profile/me");
      setProfile(response.data);
      
      // Auto-expand current month
      const currentMonth = new Date().toISOString().slice(0, 7);
      setExpandedMonths({ [currentMonth]: true });
      
      // Fetch CRM bonus if employee has crm_bonus_percent
      if (response.data?.employee?.crm_bonus_percent > 0) {
        try {
          const crmRes = await crmApi.getEmployeeBonus(response.data.employee.id, 2026);
          setCrmBonus(crmRes.data);
        } catch (err) {
          console.error("Error fetching CRM bonus:", err);
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "UAH") => {
    if (!amount && amount !== 0) return "—";
    const converted = convertAmount(amount, currency);
    return `${getCurrencySymbol()}${converted.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "paid":
        return <Badge className="bg-green-900/30 text-green-400 border-green-700 text-xs">Paid</Badge>;
      case "pending":
        return <Badge className="bg-yellow-900/30 text-yellow-400 border-yellow-700 text-xs">Pending</Badge>;
      case "partial":
        return <Badge className="bg-blue-900/30 text-blue-400 border-blue-700 text-xs">Partial</Badge>;
      default:
        return <Badge className="bg-zinc-800 text-zinc-400 text-xs">{status}</Badge>;
    }
  };

  const getMonthName = (monthKey) => {
    const [year, month] = monthKey.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  };

  // Group all payments by month
  const paymentsByMonth = useMemo(() => {
    if (!profile) return [];
    
    const grouped = {};
    
    // Add salary payments
    profile.salary_payments.forEach(p => {
      const month = p.month;
      if (!grouped[month]) {
        grouped[month] = { salary: null, bonuses: [], tasks: [] };
      }
      grouped[month].salary = p;
    });
    
    // Add bonuses
    profile.bonuses.forEach(p => {
      const month = p.month;
      if (!grouped[month]) {
        grouped[month] = { salary: null, bonuses: [], tasks: [] };
      }
      grouped[month].bonuses.push(p);
    });
    
    // Add task payments (for artists)
    profile.task_payments.forEach(p => {
      // Use paid_date month or fallback to current month
      const month = p.paid_date?.slice(0, 7) || p.client_confirmed_date?.slice(0, 7) || new Date().toISOString().slice(0, 7);
      if (!grouped[month]) {
        grouped[month] = { salary: null, bonuses: [], tasks: [] };
      }
      grouped[month].tasks.push(p);
    });

    // Merge CRM invoice collection per month (managers only). Ensures the
    // current month and any month with invoice activity shows up even without
    // a salary row. Amounts are USD from the backend (FX-normalized).
    Object.entries(profile.crm_monthly || {}).forEach(([month, c]) => {
      if (!grouped[month]) {
        grouped[month] = { salary: null, bonuses: [], tasks: [] };
      }
      grouped[month].crm = c;
    });

    // Convert to sorted array (newest first)
    return Object.entries(grouped)
      .map(([month, data]) => ({
        month,
        monthName: getMonthName(month),
        ...data,
        total: (data.salary ? convertAmount(data.salary.amount, data.salary.currency) : 0) +
               data.bonuses.reduce((sum, b) => sum + convertAmount(b.amount, b.currency), 0) +
               data.tasks.reduce((sum, t) => sum + (t.amount || 0), 0),
        isPaid: data.salary?.status === "paid" && data.bonuses.every(b => b.status === "paid"),
      }))
      .sort((a, b) => b.month.localeCompare(a.month));
  }, [profile, convertAmount]);

  // Calculate totals
  const totals = useMemo(() => {
    if (!profile) return { salary: 0, bonuses: 0, taskPayments: 0, pending: 0, salesBonusCertain: 0 };
    
    const salaryTotal = profile.salary_payments
      .filter(p => p.status === "paid")
      .reduce((sum, p) => sum + (convertAmount(p.amount, p.currency) || 0), 0);
    
    const bonusesTotal = profile.bonuses
      .filter(p => p.status === "paid")
      .reduce((sum, p) => sum + (convertAmount(p.amount, p.currency) || 0), 0);
    
    const taskPaymentsTotal = profile.task_payments
      .filter(p => p.status === "paid")
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    
    const pendingTotal = [...profile.salary_payments, ...profile.bonuses]
      .filter(p => p.status !== "paid")
      .reduce((sum, p) => sum + (convertAmount(p.amount, p.currency) || 0), 0);
    
    const salesBonusCertain = profile.sales_bonuses 
      ? (profile.sales_bonuses.personal?.bonus_paid || 0) + (profile.sales_bonuses.override?.override_bonus_paid || 0)
      : 0;
    
    return { salary: salaryTotal, bonuses: bonusesTotal, taskPayments: taskPaymentsTotal, pending: pendingTotal, salesBonusCertain };
  }, [profile, convertAmount]);

  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({ ...prev, [month]: !prev[month] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <p className="text-zinc-400">Failed to load payment data</p>
      </div>
    );
  }

  const employee = profile.employee;

  return (
    <div data-testid="my-salary-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-3">
            <Wallet className="h-7 w-7 text-green-400" />
            My Payments
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Salary, bonuses, and payment history</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Monthly Rate */}
        {employee && (
          <Card className="bg-gradient-to-br from-green-900/30 to-emerald-900/20 border-green-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-green-500 uppercase mb-2">
                <Calendar className="h-3 w-3" />
                Monthly Rate
              </div>
              <p className="text-2xl font-bold text-green-400">
                {formatCurrency(employee.salary, employee.currency)}
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                {employee.salary?.toLocaleString()} {employee.currency}
              </p>
            </CardContent>
          </Card>
        )}
        
        {/* Total Paid */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase mb-2">
              <CheckCircle2 className="h-3 w-3" />
              Total Paid
            </div>
            <p className="text-2xl font-bold text-zinc-50">
              {getCurrencySymbol()}{(totals.salary + totals.bonuses).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-zinc-500 mt-1">Salary + Bonuses</p>
          </CardContent>
        </Card>
        
        {/* Pending */}
        {totals.pending > 0 && (
          <Card className="bg-yellow-900/20 border-yellow-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-yellow-500 uppercase mb-2">
                <Clock className="h-3 w-3" />
                Pending
              </div>
              <p className="text-2xl font-bold text-yellow-400">
                {getCurrencySymbol()}{totals.pending.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
              <p className="text-xs text-zinc-500 mt-1">Awaiting payment</p>
            </CardContent>
          </Card>
        )}
        
        {/* Sales Bonus (Certain) */}
        {totals.salesBonusCertain > 0 && (
          <Card className="bg-orange-900/20 border-orange-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-orange-500 uppercase mb-2">
                <Target className="h-3 w-3" />
                Sales Bonus
              </div>
              <p className="text-2xl font-bold text-orange-400">
                {formatCurrency(totals.salesBonusCertain, "UAH")}
              </p>
              <p className="text-xs text-zinc-500 mt-1">Ready to pay</p>
            </CardContent>
          </Card>
        )}
        
        {/* CRM Bonus (for employees with crm_bonus_percent) */}
        {crmBonus && crmBonus.crm_bonus_percent > 0 && (
          <Card className="bg-purple-900/20 border-purple-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-purple-500 uppercase mb-2">
                <Database className="h-3 w-3" />
                CRM Bonus ({crmBonus.crm_bonus_percent}%)
              </div>
              <p className="text-2xl font-bold text-purple-400">
                {getCurrencySymbol()}{convertAmount(crmBonus.paid_bonus, "USD").toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                Expected: ${crmBonus.expected_bonus?.toLocaleString()} | Pending: ${crmBonus.pending_bonus?.toLocaleString()}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Work done as an executor. The AD fee already has its own section
          below, so this shows only what had no home at all. backlog 547fbd59. */}
      {taskFees && taskFees.items?.some((f) => f.kind === "executor") && (() => {
        const rows = taskFees.items.filter((f) => f.kind === "executor");
        const totals = taskFees.totals_by_kind?.executor || [];
        return (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
                <CreditCard className="h-4 w-4 text-orange-500" />
                Task fees as executor
                <span className="text-xs font-normal text-zinc-500">{rows.length} tasks</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Currencies stay apart — these rows mix UAH and USD. */}
              <div className="flex flex-wrap gap-2">
                {totals.map((tt) => (
                  <div key={tt.currency} className="rounded-lg border border-zinc-800 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{tt.currency}</div>
                    <div className="text-sm text-zinc-100 tabular-nums">
                      <span className="text-green-500">{Math.round(tt.paid).toLocaleString("uk-UA")}</span>
                      <span className="text-zinc-600"> / </span>
                      <span className={tt.unpaid > 0 ? "text-amber-400" : "text-zinc-500"}>
                        {Math.round(tt.unpaid).toLocaleString("uk-UA")}
                      </span>
                    </div>
                    <div className="text-[10px] text-zinc-500">paid / pending</div>
                  </div>
                ))}
              </div>
              <div className="rounded-lg border border-zinc-800 divide-y divide-zinc-800/60">
                {rows.map((f) => (
                  <button key={f.id} type="button" onClick={() => navigate(`/tasks/${f.task_id}`)}
                    className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-3 px-3 py-2 text-sm text-left hover:bg-zinc-800/40 transition-colors">
                    <div className="min-w-0">
                      <p className="text-zinc-100 truncate" title={f.task_name}>{f.task_name}</p>
                      <p className="text-xs text-zinc-500 truncate">
                        {f.project_name}{f.paid_date ? ` · ${f.paid_date}` : ""}
                      </p>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="text-zinc-100 tabular-nums">
                        {Math.round(f.amount).toLocaleString("uk-UA")} {f.currency}
                      </div>
                      <div className={`text-[11px] ${f.status === "paid" ? "text-green-500" : "text-amber-400"}`}>
                        {f.status === "paid" ? "paid" : f.status}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Company Profit Section (Admin Only) */}
      {profile?.user?.role === "admin" && (
        <Card className="bg-gradient-to-br from-emerald-900/20 to-green-900/10 border-emerald-800/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-zinc-50 flex items-center gap-2">
                <PiggyBank className="h-5 w-5 text-emerald-400" />
                Company Profit Distribution
              </CardTitle>
              
              {/* Month Selector */}
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={() => {
                    if (profitMonth === 1) {
                      setProfitMonth(12);
                      setProfitYear(profitYear - 1);
                    } else {
                      setProfitMonth(profitMonth - 1);
                    }
                  }}
                  className="h-8 w-8 border-zinc-700 bg-zinc-900 hover:bg-zinc-800"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md min-w-[100px] text-center">
                  <span className="text-zinc-50 text-sm font-medium">{MONTH_NAMES[profitMonth - 1]} {profitYear}</span>
                </div>
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={() => {
                    if (profitMonth === 12) {
                      setProfitMonth(1);
                      setProfitYear(profitYear + 1);
                    } else {
                      setProfitMonth(profitMonth + 1);
                    }
                  }}
                  className="h-8 w-8 border-zinc-700 bg-zinc-900 hover:bg-zinc-800"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {profitLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-emerald-500" />
              </div>
            ) : companyProfit ? (
              <>
                {/* Current Month Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div className="p-4 bg-zinc-900/50 rounded-lg text-center">
                    <p className="text-xs text-zinc-500 uppercase mb-1">Profit Fact</p>
                    <p className={cn("text-2xl font-bold", companyProfit.summary?.profit_fact >= 0 ? "text-emerald-400" : "text-red-400")}>
                      ${companyProfit.summary?.profit_fact?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                  
                  <div className="p-4 bg-green-900/20 rounded-lg text-center border border-green-700/30 relative">
                    <p className="text-xs text-green-500 uppercase mb-1">80% - Your Money</p>
                    <p className="text-2xl font-bold text-green-400">
                      ${((companyProfit.summary?.profit_fact || 0) * 0.8).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </p>
                    <Button
                      size="sm"
                      onClick={() => handleWithdraw((companyProfit.summary?.profit_fact || 0) * 0.8, "profit_80")}
                      disabled={withdrawing || (companyProfit.summary?.profit_fact || 0) <= 0}
                      className="mt-2 bg-green-600 hover:bg-green-700 text-white h-7 px-3 text-xs"
                    >
                      <Banknote className="h-3 w-3 mr-1" />
                      {withdrawing ? "..." : "Pay"}
                    </Button>
                  </div>
                  
                  <div className="p-4 bg-blue-900/20 rounded-lg text-center border border-blue-700/30">
                    <p className="text-xs text-blue-500 uppercase mb-1">20% - Development</p>
                    <p className="text-2xl font-bold text-blue-400">
                      ${((companyProfit.summary?.profit_fact || 0) * 0.2).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleWithdraw((companyProfit.summary?.profit_fact || 0) * 0.2, "profit_20")}
                      disabled={withdrawing || (companyProfit.summary?.profit_fact || 0) <= 0}
                      className="mt-2 border-blue-600 text-blue-400 hover:bg-blue-900/30 h-7 px-3 text-xs"
                    >
                      <Banknote className="h-3 w-3 mr-1" />
                      {withdrawing ? "..." : "Pay"}
                    </Button>
                  </div>
                  
                  <div className="p-4 bg-zinc-900/50 rounded-lg text-center">
                    <p className="text-xs text-zinc-500 uppercase mb-1">Profit Margin</p>
                    <p className="text-2xl font-bold text-purple-400">
                      {companyProfit.summary?.margin?.toFixed(1)}%
                    </p>
                  </div>
                </div>

                {/* Withdrawals for this month */}
                {withdrawals.filter(w => w.month === profitMonthKey).length > 0 && (
                  <div className="mb-4 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                    <p className="text-xs text-zinc-400 uppercase mb-2 flex items-center gap-1">
                      <Banknote className="h-3 w-3" />
                      Withdrawals for {MONTH_NAMES[profitMonth - 1]} {profitYear}
                    </p>
                    <div className="space-y-1">
                      {withdrawals.filter(w => w.month === profitMonthKey).map(w => (
                        <div key={w.id} className="flex items-center justify-between text-sm py-1 px-2 bg-zinc-900/50 rounded">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={cn(
                              "text-xs",
                              w.withdrawal_type === "profit_80" 
                                ? "bg-green-900/30 text-green-400 border-green-700"
                                : "bg-blue-900/30 text-blue-400 border-blue-700"
                            )}>
                              {w.withdrawal_type === "profit_80" ? "80%" : "20%"}
                            </Badge>
                            <span className="text-zinc-50 font-mono">${w.amount?.toLocaleString()}</span>
                            <span className="text-zinc-500 text-xs">{w.paid_date}</span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteWithdrawal(w.id)}
                            className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Year Summary */}
                {companyProfit.monthly_breakdown && companyProfit.monthly_breakdown.length > 0 && (() => {
                  const yearData = companyProfit.monthly_breakdown.filter(m => m.month?.startsWith(String(profitYear)));
                  const yearProfit = yearData.reduce((sum, m) => sum + (m.profit_fact || 0), 0);
                  const yearIncome = yearData.reduce((sum, m) => sum + (m.income_fact || 0), 0);
                  const yearExpenses = yearData.reduce((sum, m) => sum + (m.expenses_fact || 0), 0);
                  const yearWithdrawals = withdrawals.filter(w => w.month?.startsWith(String(profitYear)));
                  const totalWithdrawn = yearWithdrawals.reduce((sum, w) => sum + (w.amount || 0), 0);
                  
                  return (
                    <div className="mb-4 p-4 bg-gradient-to-r from-yellow-900/20 to-amber-900/10 rounded-lg border border-yellow-700/30">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-semibold text-yellow-400">📊 Year {profitYear} Total</p>
                        {totalWithdrawn > 0 && (
                          <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-700">
                            <Banknote className="h-3 w-3 mr-1" />
                            Withdrawn: ${totalWithdrawn.toLocaleString()}
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
                        <div>
                          <p className="text-xs text-zinc-500">Income</p>
                          <p className="text-lg font-bold text-green-400">${yearIncome.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500">Expenses</p>
                          <p className="text-lg font-bold text-red-400">${yearExpenses.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500">Profit</p>
                          <p className={cn("text-lg font-bold", yearProfit >= 0 ? "text-emerald-400" : "text-red-400")}>${yearProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-green-500">80% Take</p>
                          <p className="text-lg font-bold text-green-400">${(yearProfit * 0.8).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-blue-500">20% Dev</p>
                          <p className="text-lg font-bold text-blue-400">${(yearProfit * 0.2).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-orange-500">Withdrawn</p>
                          <p className="text-lg font-bold text-orange-400">${totalWithdrawn.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                
                {/* Monthly History Table */}
                {companyProfit.monthly_breakdown && companyProfit.monthly_breakdown.length > 0 && (
                  <div>
                    <p className="text-sm text-zinc-400 mb-2">Monthly History (80% / 20% split)</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-zinc-800">
                            <th className="text-left text-zinc-500 py-2 px-2">Month</th>
                            <th className="text-right text-zinc-500 py-2 px-2">Income</th>
                            <th className="text-right text-zinc-500 py-2 px-2">Expenses</th>
                            <th className="text-right text-zinc-500 py-2 px-2">Profit</th>
                            <th className="text-right text-green-500 py-2 px-2">80% Take</th>
                            <th className="text-right text-blue-500 py-2 px-2">20% Dev</th>
                            <th className="text-right text-zinc-500 py-2 px-2">Margin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {companyProfit.monthly_breakdown.slice(0, 12).map((m) => (
                            <tr key={m.month} className={cn("border-b border-zinc-800/50 hover:bg-zinc-800/30", m.month === profitMonthKey && "bg-emerald-900/20")}>
                              <td className="py-2 px-2 text-zinc-50">{getMonthName(m.month)}</td>
                              <td className="py-2 px-2 text-right text-green-400 font-mono">${m.income_fact?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                              <td className="py-2 px-2 text-right text-red-400 font-mono">${m.expenses_fact?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                              <td className={cn("py-2 px-2 text-right font-mono font-bold", m.profit_fact >= 0 ? "text-emerald-400" : "text-red-400")}>
                                ${m.profit_fact?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </td>
                              <td className="py-2 px-2 text-right text-green-400 font-mono">
                                ${((m.profit_fact || 0) * 0.8).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </td>
                              <td className="py-2 px-2 text-right text-blue-400 font-mono">
                                ${((m.profit_fact || 0) * 0.2).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </td>
                              <td className="py-2 px-2 text-right text-zinc-400">{m.margin?.toFixed(0)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-zinc-500 text-center py-4">No profit data available</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sales Bonuses (for sales managers) - Compact */}
      {profile.sales_bonuses && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-zinc-50 flex items-center gap-2">
              <Target className="h-5 w-5 text-orange-400" />
              Sales Commission (10%)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Personal Sales Summary */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  My Sales ({profile.sales_bonuses.personal.projects_count} projects)
                </h4>
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-3 bg-zinc-800/50 rounded-lg text-center">
                    <p className="text-xs text-zinc-500 mb-1">All</p>
                    <p className="text-sm font-bold text-zinc-300">{formatCurrency(profile.sales_bonuses.personal.bonus_all, "UAH")}</p>
                  </div>
                  <div className="p-3 bg-amber-900/20 rounded-lg text-center border border-amber-800/30">
                    <p className="text-xs text-amber-500 mb-1">≥50%</p>
                    <p className="text-sm font-bold text-amber-400">{formatCurrency(profile.sales_bonuses.personal.bonus_high_prob, "UAH")}</p>
                  </div>
                  <div className="p-3 bg-green-900/20 rounded-lg text-center border border-green-800/30">
                    <p className="text-xs text-green-500 mb-1">Paid ✓</p>
                    <p className="text-sm font-bold text-green-400">{formatCurrency(profile.sales_bonuses.personal.bonus_paid, "UAH")}</p>
                  </div>
                </div>
              </div>
              
              {/* Override (if applicable) */}
              {profile.sales_bonuses.override && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                    <Percent className="h-4 w-4" />
                    Override ({profile.sales_bonuses.override.managers_count} managers)
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 bg-purple-900/20 rounded-lg text-center border border-purple-800/30">
                      <p className="text-xs text-purple-500 mb-1">Expected ({profile.sales_bonuses.override.override_rate_plan}%)</p>
                      <p className="text-sm font-bold text-purple-400">{formatCurrency(profile.sales_bonuses.override.override_bonus_expected, "UAH")}</p>
                    </div>
                    <div className="p-3 bg-green-900/20 rounded-lg text-center border border-green-800/30">
                      <p className="text-xs text-green-500 mb-1">Paid ✓</p>
                      <p className="text-sm font-bold text-green-400">{formatCurrency(profile.sales_bonuses.override.override_bonus_paid, "UAH")}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Total */}
            <div className="mt-4 pt-4 border-t border-zinc-800 flex justify-between items-center">
              <span className="text-sm text-zinc-400 flex items-center gap-2">
                <PiggyBank className="h-4 w-4 text-green-400" />
                Total Ready to Pay
              </span>
              <span className="text-xl font-bold text-green-400">
                {formatCurrency(
                  (profile.sales_bonuses.personal.bonus_paid || 0) + 
                  (profile.sales_bonuses.override?.override_bonus_paid || 0),
                  "UAH"
                )}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Art Director Bonuses */}
      {profile.art_director_bonuses && profile.art_director_bonuses.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-zinc-50 flex items-center gap-2">
              <Award className="h-5 w-5 text-purple-400" />
              Art Director Bonuses ({profile.art_director_bonuses.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3 bg-zinc-800/50 rounded-lg text-center">
                <p className="text-xs text-zinc-500 mb-1">Всього</p>
                <p className="text-sm font-bold text-zinc-300">
                  {formatCurrency(
                    profile.art_director_bonuses.reduce((sum, b) => {
                      const amountUah = b.currency === "USD" ? b.amount * (profile.rates?.USD || 41.5) : b.amount;
                      return sum + amountUah;
                    }, 0),
                    "UAH"
                  )}
                </p>
              </div>
              <div className="p-3 bg-amber-900/20 rounded-lg text-center border border-amber-800/30">
                <p className="text-xs text-amber-500 mb-1">Очікує</p>
                <p className="text-sm font-bold text-amber-400">
                  {formatCurrency(
                    profile.art_director_bonuses.filter(b => b.status === "pending").reduce((sum, b) => {
                      const amountUah = b.currency === "USD" ? b.amount * (profile.rates?.USD || 41.5) : b.amount;
                      return sum + amountUah;
                    }, 0),
                    "UAH"
                  )}
                </p>
              </div>
              <div className="p-3 bg-green-900/20 rounded-lg text-center border border-green-800/30">
                <p className="text-xs text-green-500 mb-1">Виплачено</p>
                <p className="text-sm font-bold text-green-400">
                  {formatCurrency(
                    profile.art_director_bonuses.filter(b => b.status === "paid").reduce((sum, b) => {
                      const amountUah = b.currency === "USD" ? b.amount * (profile.rates?.USD || 41.5) : b.amount;
                      return sum + amountUah;
                    }, 0),
                    "UAH"
                  )}
                </p>
              </div>
            </div>
            
            {/* Bonus List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {profile.art_director_bonuses.map(bonus => (
                <div 
                  key={bonus.id} 
                  className={cn(
                    "p-3 rounded-lg border",
                    bonus.status === "paid" 
                      ? "bg-green-900/10 border-green-800/30" 
                      : "bg-zinc-800/50 border-zinc-700"
                  )}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-50 truncate">{bonus.task_name}</p>
                      <p className="text-xs text-zinc-400">{bonus.project_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-zinc-500">
                          Ціна артиста: ₴{bonus.artist_price_uah?.toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </span>
                        <span className="text-xs text-purple-400">
                          × {bonus.bonus_percent}%
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={cn(
                        "text-sm font-bold",
                        bonus.status === "paid" ? "text-green-400" : "text-amber-400"
                      )}>
                        {bonus.currency === "USD" ? "$" : "₴"}{bonus.amount?.toLocaleString(undefined, {maximumFractionDigits: 2})}
                      </p>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "text-xs mt-1",
                          bonus.status === "paid" 
                            ? "border-green-600 text-green-400" 
                            : "border-amber-600 text-amber-400"
                        )}
                      >
                        {bonus.status === "paid" ? "Виплачено" : "Очікує"}
                      </Badge>
                      {bonus.bonus_month && (
                        <p className="text-xs text-zinc-500 mt-1">{bonus.bonus_month}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Payment History */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-zinc-50 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-sky-400" />
            Payment History by Month
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {paymentsByMonth.length === 0 ? (
            <div className="py-8 text-center">
              <Wallet className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No payment history yet</p>
            </div>
          ) : (
            paymentsByMonth.map(monthData => {
              const isExpanded = expandedMonths[monthData.month];
              const hasSalary = monthData.salary;
              const hasBonuses = monthData.bonuses.length > 0;
              const hasTasks = monthData.tasks.length > 0;
              
              return (
                <div 
                  key={monthData.month} 
                  className={cn(
                    "border rounded-lg overflow-hidden transition-all",
                    monthData.isPaid ? "border-green-800/30 bg-green-900/5" : "border-zinc-800 bg-zinc-800/20"
                  )}
                >
                  {/* Month Header */}
                  <button
                    onClick={() => toggleMonth(monthData.month)}
                    className="w-full flex items-center justify-between p-4 hover:bg-zinc-800/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-zinc-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-zinc-500" />
                      )}
                      <span className="font-semibold text-zinc-50">{monthData.monthName}</span>
                      {monthData.isPaid && (
                        <CheckCircle2 className="h-4 w-4 text-green-400" />
                      )}
                      {!monthData.isPaid && hasSalary && (
                        <Clock className="h-4 w-4 text-yellow-400" />
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Badges */}
                      <div className="flex items-center gap-2">
                        {hasSalary && (
                          <span className="text-xs bg-sky-900/30 text-sky-400 px-2 py-0.5 rounded">Salary</span>
                        )}
                        {hasBonuses && (
                          <span className="text-xs bg-amber-900/30 text-amber-400 px-2 py-0.5 rounded">{monthData.bonuses.length} Bonus{monthData.bonuses.length > 1 ? 'es' : ''}</span>
                        )}
                        {hasTasks && (
                          <span className="text-xs bg-purple-900/30 text-purple-400 px-2 py-0.5 rounded">{monthData.tasks.length} Task{monthData.tasks.length > 1 ? 's' : ''}</span>
                        )}
                        {monthData.crm?.count > 0 && (
                          <span className="text-xs bg-emerald-900/30 text-emerald-400 px-2 py-0.5 rounded">{monthData.crm.count} Invoice{monthData.crm.count > 1 ? 's' : ''}</span>
                        )}
                      </div>
                      
                      {/* Total */}
                      <span className={cn(
                        "font-bold font-mono text-lg",
                        monthData.isPaid ? "text-green-400" : "text-zinc-50"
                      )}>
                        {getCurrencySymbol()}{monthData.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  </button>
                  
                  {/* Month Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-zinc-800/50">
                      {/* Salary */}
                      {hasSalary && (
                        <div className="pt-3">
                          <div className="flex items-center justify-between p-3 bg-sky-900/10 rounded-lg border border-sky-800/20">
                            <div className="flex items-center gap-3">
                              <CreditCard className="h-4 w-4 text-sky-400" />
                              <div>
                                <p className="text-sm font-medium text-zinc-50">Salary</p>
                                {monthData.salary.paid_date && (
                                  <p className="text-xs text-zinc-500">Paid: {monthData.salary.paid_date}</p>
                                )}
                                {monthData.salary.is_projected && (
                                  <p className="text-xs text-zinc-500">Projected — not yet generated</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {getStatusBadge(monthData.salary.status)}
                              <span className="font-mono font-bold text-sky-400">
                                {formatCurrency(monthData.salary.amount, monthData.salary.currency)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Bonuses */}
                      {hasBonuses && monthData.bonuses.map((bonus, idx) => (
                        <div key={bonus.id || idx} className="flex items-center justify-between p-3 bg-amber-900/10 rounded-lg border border-amber-800/20">
                          <div className="flex items-center gap-3">
                            <Award className="h-4 w-4 text-amber-400" />
                            <div>
                              <p className="text-sm font-medium text-zinc-50">{bonus.bonus_type || 'Bonus'}</p>
                              {bonus.notes && (
                                <p className="text-xs text-zinc-500 truncate max-w-[200px]">{bonus.notes}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {getStatusBadge(bonus.status)}
                            <span className="font-mono font-bold text-amber-400">
                              {formatCurrency(bonus.amount, bonus.currency)}
                            </span>
                          </div>
                        </div>
                      ))}
                      
                      {/* Task Payments */}
                      {hasTasks && monthData.tasks.map((task, idx) => (
                        <div key={task.id || idx} className={cn(
                          "flex items-center justify-between p-3 rounded-lg border",
                          task.is_overdue ? "bg-red-900/10 border-red-800/20" : "bg-purple-900/10 border-purple-800/20"
                        )}>
                          <div className="flex items-center gap-3">
                            <DollarSign className="h-4 w-4 text-purple-400" />
                            <div>
                              <p className="text-sm font-medium text-zinc-50">{task.task_name}</p>
                              <p className="text-xs text-zinc-500">{task.project_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {task.is_overdue && <AlertCircle className="h-4 w-4 text-red-400" />}
                            {getStatusBadge(task.status)}
                            <span className="font-mono font-bold text-purple-400">
                              ${task.amount?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}

                      {/* CRM invoice collection (managers) — informational, not
                          part of the personal pay total above. */}
                      {monthData.crm && (monthData.crm.count > 0 || monthData.crm.paid_usd > 0) && (
                        <div className="grid grid-cols-2 gap-3 pt-1">
                          <div className="p-3 bg-emerald-900/10 rounded-lg border border-emerald-800/20">
                            <p className="text-[11px] uppercase text-emerald-500/80 mb-1">Invoices collected</p>
                            <p className="font-mono font-bold text-emerald-400">
                              {getCurrencySymbol()}{convertAmount(monthData.crm.paid_usd, "USD").toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </p>
                            <p className="text-xs text-zinc-500">{monthData.crm.count} invoice{monthData.crm.count === 1 ? "" : "s"} paid</p>
                          </div>
                          {monthData.crm.debt_usd > 0 && (
                            <div className="p-3 bg-orange-900/10 rounded-lg border border-orange-800/20">
                              <p className="text-[11px] uppercase text-orange-500/80 mb-1">Debt collected</p>
                              <p className="font-mono font-bold text-orange-400">
                                {getCurrencySymbol()}{convertAmount(monthData.crm.debt_usd, "USD").toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </p>
                              <p className="text-xs text-zinc-500">for prior months</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* No data message */}
      {!employee && paymentsByMonth.length === 0 && !profile.sales_bonuses && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12 text-center">
            <Wallet className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No payment data available</p>
            <p className="text-zinc-600 text-sm mt-1">Your salary and bonus payments will appear here</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MySalaryPage;
